import { Fragment, createContext, useCallback, useContext, useMemo } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useTheme, Menu } from '@web-chat-core/ui-kit';
import type { MenuToggleButtonProps } from '@web-chat-core/ui-kit/core/Menu/MenuToggleButton';
import { ObstacleId } from 'features/MutedSpeechDetection/guardController';
import { ControlButtonMenuItem } from '../ControlButtonMenuItem/ControlButtonMenuItem';
import type { DeviceKind } from '../AudioButton/helper';
import { MenuTrigger, MenuTriggerIcon, Separator } from './view';

const TriggerStateContext = createContext<{ active: boolean; disabled: boolean }>({
    active: true,
    disabled: false,
});

const ControlButtonToggle = ({ children, ref }: MenuToggleButtonProps) => {
    const { active, disabled } = useContext(TriggerStateContext);
    return (
        <DropdownMenuPrimitive.Trigger asChild>
            <MenuTrigger ref={ref} $on={active} disabled={disabled}>
                {children}
            </MenuTrigger>
        </DropdownMenuPrimitive.Trigger>
    );
};
ControlButtonToggle.displayName = 'ControlButtonToggle';

export type MenuItem = {
    type: 'title' | 'label' | 'element' | 'menu';
    active?: boolean;
    text?: string;
    element?: React.ComponentType;
    withSeparator?: boolean;
    fullWidth?: boolean;
    kind?: DeviceKind;
};

interface ControlButtonMenuProps {
    disabled?: boolean;
    menuItems?: MenuItem[];
    onMenuItemClick?: (item: MenuItem) => void;
    active?: boolean;
    withoutShadow?: boolean;
    menuId?: ObstacleId;
}

const CONTENT_STYLE = { width: 248 };

const contentProps = {
    side: 'top' as const,
    sideOffset: 4,
    align: 'end' as const,
    alignOffset: -76,
    avoidCollisions: false,
    // hideWhenDetached: true - скрывает меню, если триггер-компонент в какой-то момент уходит из viewport'а.
    // Но здесь выставлено значение false, поскольку это фиксит редки баг, когда при ресайзе страницы radix может посчитать
    // триггер-компонент обрезанным (хотя он виден) и скрыть меню.
    // В нашем случае hideWhenDetached: false - безопасная настройка, поскольку триггер-компонент не исчезает из viewport'а.
    hideWhenDetached: false,
    style: CONTENT_STYLE,
};

export const ControlButtonMenu = ({
    disabled,
    menuItems,
    onMenuItemClick,
    active = true,
    withoutShadow,
    menuId,
}: ControlButtonMenuProps) => {
    const theme = useTheme();

    const handleMenuClick = useCallback(
        (item: MenuItem) => {
            onMenuItemClick?.(item);
        },
        [onMenuItemClick],
    );

    const triggerState = useMemo(() => ({ active, disabled: disabled ?? false }), [active, disabled]);

    if (!menuItems || menuItems.length === 0) return null;

    const colorIcon = active ? theme.root.colors.basicColors['55%'] : theme.root.colors.background.badgeMuted;

    return (
        <TriggerStateContext.Provider value={triggerState}>
            <Menu
                menuId={menuId}
                ToggleButton={ControlButtonToggle}
                toggleButtonContent={<MenuTriggerIcon icon="icUp16" color={colorIcon} size={16} />}
                disabled={disabled}
                contentProps={contentProps}
                withoutShadow={withoutShadow}
            >
                {menuItems.map((item, key) => {
                    if (!item || typeof item !== 'object') return null;
                    return (
                        <Fragment key={key}>
                            {item.withSeparator && <Separator />}
                            <ControlButtonMenuItem item={item} onSelect={handleMenuClick} />
                        </Fragment>
                    );
                })}
            </Menu>
        </TriggerStateContext.Provider>
    );
};

ControlButtonMenu.displayName = 'ControlButtonMenu';
